// src/pages/OrderTrackingPage.jsx - İADE ÖZELLİKLİ VERSİYON
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Package, Mail, Search, ChevronRight, AlertCircle,
  Truck, CheckCircle, XCircle, Clock, MapPin, CreditCard,
  Phone, Calendar, ArrowLeft, RotateCcw, X
} from 'lucide-react';
import { getOrderByNumberAndEmail } from '../services/orderService';
import { requestOrderReturn } from '../services/returnService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

export default function OrderTrackingPage() {
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [searched, setSearched] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!orderNumber.trim() || !email.trim()) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Geçerli bir e-posta adresi girin');
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const result = await getOrderByNumberAndEmail(orderNumber.trim(), email.trim());

      if (result.success) {
        setOrder(result.order);
        
        const customerType = result.order.customerType === 'guest' ? 'Misafir' : 'Üye';
        toast.success(`Sipariş bulundu! (${customerType} Sipariş)`, {
          duration: 3000,
          icon: '✅'
        });
      } else {
        setOrder(null);
        toast.error('Sipariş bulunamadı veya e-posta adresi hatalı', {
          duration: 4000
        });
      }
    } catch (error) {
      console.error('Sipariş sorgulama hatası:', error);
      setOrder(null);
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ İade edilebilir mi kontrol
  const canReturn = () => {
    if (!order) return false;
    if (order.returnStatus) return false;
    if (!['shipped', 'delivered'].includes(order.status)) return false;

    if (order.status === 'delivered') {
      if (!order.deliveredAt) return true;

      try {
        let deliveryDate;
        
        if (order.deliveredAt.toDate && typeof order.deliveredAt.toDate === 'function') {
          deliveryDate = order.deliveredAt.toDate();
        } else if (order.deliveredAt instanceof Date) {
          deliveryDate = order.deliveredAt;
        } else if (typeof order.deliveredAt === 'string') {
          deliveryDate = new Date(order.deliveredAt);
        } else {
          return true;
        }

        const now = new Date();
        const daysPassed = Math.floor((now - deliveryDate) / (1000 * 60 * 60 * 24));
        return daysPassed <= 7;
      } catch (error) {
        console.error('Tarih parse hatası:', error);
        return true;
      }
    }

    return true;
  };

  const handleReturnRequest = () => {
    setShowReturnModal(true);
  };

  const reloadOrder = async () => {
    if (!orderNumber || !email) return;
    
    const result = await getOrderByNumberAndEmail(orderNumber.trim(), email.trim());
    if (result.success) {
      setOrder(result.order);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      shipped: '#8b5cf6',
      delivered: '#22c55e',
      cancelled: '#ef4444',
      returned: '#f59e0b'
    };
    return colors[status] || '#868e96';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Sipariş Alındı',
      processing: 'Hazırlanıyor',
      shipped: 'Kargoya Verildi',
      delivered: 'Teslim Edildi',
      cancelled: 'İptal Edildi',
      returned: 'İade Edildi'
    };
    return texts[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      processing: Package,
      shipped: Truck,
      delivered: CheckCircle,
      cancelled: XCircle,
      returned: RotateCcw
    };
    const Icon = icons[status] || Package;
    return <Icon size={20} />;
  };

  return (
    <div className="order-tracking-page">
      <Style />
      <Header />

      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Anasayfa</Link>
          <ChevronRight size={16} />
          <span>Sipariş Takibi</span>
        </div>

        <div className="tracking-container">
          {/* Arama Formu */}
          <div className="search-section">
            <div className="search-header">
              <div className="header-icon">
                <Package size={32} />
              </div>
              <h1>Sipariş Takibi</h1>
              <p>Sipariş numaranız ve e-posta adresiniz ile siparişinizi takip edebilirsiniz</p>
            </div>

            <form onSubmit={handleSearch} className="search-form">
              <div className="form-group">
                <label htmlFor="orderNumber">
                  <Package size={18} />
                  Sipariş Numarası
                </label>
                <input
                  type="text"
                  id="orderNumber"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="ORD-1234567890-123"
                  className="form-input"
                />
                <small className="form-hint">
                  Sipariş numaranız e-posta adresinize gönderildi
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <Mail size={18} />
                  E-posta Adresi
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className="form-input"
                />
                <small className="form-hint">
                  Sipariş verirken kullandığınız e-posta adresi
                </small>
              </div>

              <button type="submit" className="search-btn" disabled={loading}>
                {loading ? (
                  <>
                    <div className="spinner-small"></div>
                    Aranıyor...
                  </>
                ) : (
                  <>
                    <Search size={20} />
                    Siparişi Sorgula
                  </>
                )}
              </button>
            </form>

            <div className="help-section">
              <AlertCircle size={16} />
              <div>
                <strong>Sipariş numaranızı bulamıyor musunuz?</strong>
                <p>Sipariş onay e-postanızı kontrol edin veya müşteri hizmetleri ile iletişime geçin.</p>
              </div>
            </div>
          </div>

          {/* Sipariş Sonuçları */}
          {searched && !loading && (
            <>
              {order ? (
                <div className="order-details-section">
                  {/* İade Talep Durumu Banner */}
                  {order.returnStatus && (
                    <div className={`request-status-banner ${order.returnStatus}`}>
                      <RotateCcw size={24} />
                      <div>
                        <h4>İade Talebi Durumu: {getReturnStatusLabel(order.returnStatus)}</h4>
                        {order.returnRequest && (
                          <p>{order.returnRequest.reason}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Durum Banner */}
                  <div 
                    className="status-banner" 
                    style={{ 
                      background: getStatusColor(order.status) + '15',
                      borderColor: getStatusColor(order.status)
                    }}
                  >
                    <div 
                      className="status-icon" 
                      style={{ background: getStatusColor(order.status) }}
                    >
                      {getStatusIcon(order.status)}
                    </div>
                    <div className="status-info">
                      <h3 style={{ color: getStatusColor(order.status) }}>
                        {getStatusText(order.status)}
                      </h3>
                      <p>Sipariş Tarihi: {new Date(order.createdAt).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}</p>
                    </div>

                    {/* ✅ İADE BUTONU */}
                    {canReturn() && (
                      <div className="action-buttons">
                        <button className="return-btn" onClick={handleReturnRequest}>
                          <RotateCcw size={18} />
                          İade Talebi Oluştur
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="details-grid">
                    {/* Zaman Çizelgesi */}
                    <div className="details-card timeline-card">
                      <h3>
                        <Clock size={20} />
                        Sipariş Durumu
                      </h3>
                      
                      <div className="timeline">
                        {order.timeline && order.timeline.slice().reverse().map((event, index) => (
                          <div key={index} className="timeline-item">
                            <div className="timeline-dot"></div>
                            <div className="timeline-content">
                              <h4>{event.action}</h4>
                              <p className="timeline-date">
                                {new Date(event.date).toLocaleDateString('tr-TR', {
                                  day: 'numeric',
                                  month: 'long',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Teslimat Bilgileri */}
                    <div className="details-card">
                      <h3>
                        <MapPin size={20} />
                        Teslimat Bilgileri
                      </h3>
                      <div className="card-content">
                        <p className="recipient-name">
                          {order.shippingAddress?.firstName} {order.shippingAddress?.lastName}
                        </p>
                        <p>{order.shippingAddress?.address}</p>
                        <p>{order.shippingAddress?.district} / {order.shippingAddress?.city}</p>
                        {order.shippingAddress?.zipCode && (
                          <p>Posta Kodu: {order.shippingAddress.zipCode}</p>
                        )}
                        <div className="contact-info">
                          <div className="contact-item">
                            <Phone size={14} />
                            <span>{order.shippingAddress?.phone}</span>
                          </div>
                          <div className="contact-item">
                            <Mail size={14} />
                            <span>{order.shippingAddress?.email}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ödeme Bilgileri */}
                    <div className="details-card">
                      <h3>
                        <CreditCard size={20} />
                        Ödeme Bilgileri
                      </h3>
                      <div className="card-content">
                        <div className="payment-row">
                          <span>Ödeme Yöntemi:</span>
                          <strong>Kredi Kartı</strong>
                        </div>
                        <div className="payment-row">
                          <span>Ödeme Durumu:</span>
                          <strong className={order.paymentStatus === 'paid' ? 'paid' : 'pending'}>
                            {order.paymentStatus === 'paid' ? 'Ödendi ✓' : 'Bekliyor'}
                          </strong>
                        </div>
                        {order.paymentResult?.authCode && (
                          <div className="payment-row">
                            <span>Onay Kodu:</span>
                            <strong>{order.paymentResult.authCode}</strong>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Sipariş Özeti */}
                    <div className="details-card full-width">
                      <h3>
                        <Package size={20} />
                        Sipariş Özeti
                      </h3>
                      <div className="order-items">
                        {order.items && order.items.map((item, index) => (
                          <div key={index} className="order-item">
                            <img src={item.productImage} alt={item.productName} />
                            <div className="item-details">
                              <h4>{item.productName}</h4>
                              <p>Adet: {item.quantity}</p>
                            </div>
                            <div className="item-price">
                              {(item.price * item.quantity).toFixed(2)}₺
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="order-totals">
                        <div className="total-row">
                          <span>Ara Toplam:</span>
                          <span>{order.subtotal?.toFixed(2)}₺</span>
                        </div>
                        <div className="total-row">
                          <span>Kargo:</span>
                          <span>
                            {order.shippingCost === 0 
                              ? 'Ücretsiz' 
                              : `${order.shippingCost?.toFixed(2)}₺`}
                          </span>
                        </div>
                        <div className="total-row final">
                          <span>Toplam:</span>
                          <strong>{order.total?.toFixed(2)}₺</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-order-section">
                  <XCircle size={64} />
                  <h3>Sipariş Bulunamadı</h3>
                  <p>Girdiğiniz bilgilerle eşleşen bir sipariş bulunamadı.</p>
                  <p className="hint">Lütfen sipariş numaranızı ve e-posta adresinizi kontrol edin.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />

      {/* ✅ İADE MODAL */}
      {showReturnModal && order && (
        <ReturnOrderModal
          order={order}
          onClose={() => setShowReturnModal(false)}
          onSuccess={reloadOrder}
        />
      )}
    </div>
  );
}

// ✅ Helper fonksiyonlar
function getReturnStatusLabel(status) {
  const labels = {
    pending: 'Beklemede',
    approved: 'Onaylandı - Kargo Bekliyor',
    in_transit: 'Kargoda',
    received: 'Ürün Teslim Alındı',
    completed: 'Tamamlandı - İade Yapıldı',
    rejected: 'Reddedildi'
  };
  return labels[status] || status;
}

// ✅ İADE MODAL (OrderDetailPage'dekiyle aynı)
function ReturnOrderModal({ order, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const [hasDefect, setHasDefect] = useState(false);
  const [defectDescription, setDefectDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const returnReasons = [
    'Ürün hasarlı/kusurlu geldi',
    'Yanlış ürün geldi',
    'Ürün açıklamaya uygun değil',
    'Ürün beklentimi karşılamadı',
    'Geç teslim edildi',
    'Fikir değiştirdim',
    'Diğer'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason) {
      toast.error('Lütfen bir iade nedeni seçin');
      return;
    }

    if (hasDefect && !defectDescription.trim()) {
      toast.error('Lütfen kusur/hasar açıklaması yapın');
      return;
    }

    setLoading(true);

    try {
      const userId = localStorage.getItem('userId') || 'anonymous';

      const result = await requestOrderReturn(order.id, {
        reason,
        reasonDetail,
        hasDefect,
        defectDescription: hasDefect ? defectDescription : '',
        userId
      });

      if (result.success) {
        toast.success(result.message);
        onClose();
        onSuccess();
      } else {
        toast.error(result.error || 'İade talebi oluşturulamadı');
      }
    } catch (error) {
      console.error('İade talebi hatası:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <RotateCcw size={24} />
            İade Talebi Oluştur
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="info-box warning">
              <AlertCircle size={20} />
              <div>
                <p><strong>BİLGİ:</strong> İade talebiniz incelenecek</p>
                <p className="small-text">Onay sonrası kargo bilgileri size iletilecektir.</p>
              </div>
            </div>

            <div className="info-box">
              <AlertCircle size={20} />
              <div>
                <p>Sipariş numarası: <strong>#{order.orderNumber}</strong></p>
                <p>Toplam tutar: <strong>{order.total?.toFixed(2)}₺</strong></p>
                {order.status === 'delivered' && (
                  <p className="small-text">Teslim tarihinden itibaren 7 gün içinde iade yapabilirsiniz.</p>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>İade Nedeni *</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              >
                <option value="">Seçiniz...</option>
                {returnReasons.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Açıklama</label>
              <textarea
                value={reasonDetail}
                onChange={(e) => setReasonDetail(e.target.value)}
                placeholder="İade nedeninizi detaylı açıklayın..."
                rows={3}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={hasDefect}
                  onChange={(e) => setHasDefect(e.target.checked)}
                />
                <span>Üründe kusur/hasar var</span>
              </label>
            </div>

            {hasDefect && (
              <div className="form-group">
                <label>Kusur/Hasar Açıklaması *</label>
                <textarea
                  value={defectDescription}
                  onChange={(e) => setDefectDescription(e.target.value)}
                  placeholder="Lütfen kusur veya hasarı detaylı açıklayın..."
                  rows={3}
                  required
                />
              </div>
            )}

            <div className="warning-box">
              <AlertCircle size={18} />
              <div>
                <p><strong>İade Süreci:</strong></p>
                <ol>
                  <li>İade talebiniz satıcı tarafından incelenecek</li>
                  <li>Onaylandığında size kargo bilgileri gönderilecek</li>
                  <li>Ürünü belirlenen adrese göndereceksiniz</li>
                  <li>Ürün kontrolü sonrası ödeme iadeniz yapılacak</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Vazgeç
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Gönderiliyor...' : 'İade Talebi Gönder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Style() {
  return <style>{`
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    .order-tracking-page {
      min-height: 100vh;
      background: #f8f9fa;
    }

    .container {
      max-width: 1320px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 20px 0;
      font-size: 14px;
    }

    .breadcrumb a {
      color: #868e96;
      text-decoration: none;
      transition: color 0.2s;
    }

    .breadcrumb a:hover {
      color: #E5332A;
    }

    .tracking-container {
      max-width: 1200px;
      margin: 0 auto 60px;
    }

    .search-section {
      background: #fff;
      border-radius: 24px;
      padding: 48px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.06);
      margin-bottom: 32px;
    }

    .search-header {
      text-align: center;
      margin-bottom: 40px;
    }

    .header-icon {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #fff5f5, #ffe8e8);
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 24px;
      color: #E5332A;
    }

    .search-header h1 {
      font-size: 32px;
      color: #2B2B2B;
      margin-bottom: 12px;
    }

    .search-header p {
      font-size: 16px;
      color: #868e96;
      max-width: 500px;
      margin: 0 auto;
      line-height: 1.6;
    }

    .search-form {
      max-width: 500px;
      margin: 0 auto;
    }

    .form-group {
      margin-bottom: 24px;
    }

    .form-group label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #2B2B2B;
      margin-bottom: 8px;
    }

    .form-group label svg {
      color: #E5332A;
    }

    .form-input,
    .form-group select,
    .form-group textarea {
      width: 100%;
      padding: 14px 16px;
      border: 2px solid #e9ecef;
      border-radius: 12px;
      font-size: 15px;
      transition: all 0.2s;
      font-family: inherit;
    }

    .form-input:focus,
    .form-group select:focus,
    .form-group textarea:focus {
      outline: none;
      border-color: #E5332A;
      background: #fff;
    }

    .form-group textarea {
      resize: vertical;
      min-height: 80px;
    }

    .form-hint {
      display: block;
      margin-top: 6px;
      font-size: 12px;
      color: #868e96;
    }

    .search-btn {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      background: #E5332A;
      color: #fff;
      border: none;
      padding: 16px;
      border-radius: 12px;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s;
      margin-top: 32px;
    }

    .search-btn:hover:not(:disabled) {
      background: #c72a22;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(229, 51, 42, 0.3);
    }

    .search-btn:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .spinner-small {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .help-section {
      display: flex;
      gap: 12px;
      background: #f0f9ff;
      border: 1px solid #bfdbfe;
      border-radius: 12px;
      padding: 16px;
      margin-top: 32px;
    }

    .help-section svg {
      color: #3b82f6;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .help-section strong {
      display: block;
      font-size: 14px;
      color: #1e3a8a;
      margin-bottom: 4px;
    }

    .help-section p {
      font-size: 13px;
      color: #475569;
      margin: 0;
      line-height: 1.5;
    }

    .order-details-section {
      animation: fadeIn 0.5s ease-out;
    }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* ✅ İADE TALEP BANNER */
    .request-status-banner {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      padding: 20px 24px;
      border-radius: 16px;
      margin-bottom: 24px;
      border: 2px solid;
    }

    .request-status-banner.pending {
      background: #fffbeb;
      border-color: #f59e0b;
      color: #92400e;
    }

    .request-status-banner.approved,
    .request-status-banner.in_transit,
    .request-status-banner.received {
      background: #f0fdf4;
      border-color: #22c55e;
      color: #166534;
    }

    .request-status-banner.rejected {
      background: #fef2f2;
      border-color: #ef4444;
      color: #991b1b;
    }

    .request-status-banner.completed {
      background: #dcfce7;
      border-color: #22c55e;
      color: #166534;
    }

    .request-status-banner h4 {
      font-size: 16px;
      margin-bottom: 4px;
      font-weight: 700;
    }

    .request-status-banner p {
      font-size: 14px;
      opacity: 0.9;
      margin: 0;
    }

    .status-banner {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 24px;
      border-radius: 20px;
      border: 2px solid;
      margin-bottom: 24px;
      background: #fff;
    }

    .status-icon {
      width: 56px;
      height: 56px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      flex-shrink: 0;
    }

    .status-info {
      flex: 1;
    }

    .status-info h3 {
      font-size: 22px;
      margin-bottom: 6px;
    }

    .status-info p {
      font-size: 14px;
      color: #868e96;
      margin: 0;
    }

    /* ✅ İADE BUTON */
    .action-buttons {
      display: flex;
      gap: 12px;
      margin-left: auto;
    }

    .return-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      background: #fff;
      color: #f59e0b;
      border: 2px solid #f59e0b;
      border-radius: 12px;
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }

    .return-btn:hover {
      background: #fffbeb;
      transform: translateY(-1px);
    }

    .details-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 24px;
    }

    .details-card {
      background: #fff;
      border-radius: 20px;
      padding: 28px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }

    .details-card.full-width {
      grid-column: 1 / -1;
    }

    .details-card.timeline-card {
      grid-row: span 2;
    }

    .details-card h3 {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 18px;
      margin-bottom: 20px;
      color: #2B2B2B;
    }

    .details-card h3 svg {
      color: #E5332A;
    }

    .card-content {
      font-size: 14px;
      line-height: 1.8;
      color: #495057;
    }

    .recipient-name {
      font-weight: 600;
      color: #2B2B2B;
      margin-bottom: 8px;
    }

    .contact-info {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #e9ecef;
    }

    .contact-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
    }

    .contact-item svg {
      color: #868e96;
      flex-shrink: 0;
    }

    .timeline {
      position: relative;
    }

    .timeline-item {
      position: relative;
      padding-left: 32px;
      padding-bottom: 24px;
    }

    .timeline-item:last-child {
      padding-bottom: 0;
    }

    .timeline-item:not(:last-child)::before {
      content: '';
      position: absolute;
      left: 6px;
      top: 20px;
      bottom: -4px;
      width: 2px;
      background: #e9ecef;
    }

    .timeline-dot {
      position: absolute;
      left: 0;
      top: 4px;
      width: 14px;
      height: 14px;
      background: #E5332A;
      border-radius: 50%;
      border: 3px solid #fff;
      box-shadow: 0 0 0 2px #e9ecef;
    }

    .timeline-content h4 {
      font-size: 15px;
      color: #2B2B2B;
      margin-bottom: 4px;
    }

    .timeline-date {
      font-size: 13px;
      color: #868e96;
      margin: 0;
    }

    .payment-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 12px;
      font-size: 14px;
    }

    .payment-row span {
      color: #868e96;
    }

    .payment-row strong.paid {
      color: #22c55e;
    }

    .payment-row strong.pending {
      color: #f59e0b;
    }

    .order-items {
      margin-bottom: 20px;
    }

    .order-item {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px;
      background: #f8f9fa;
      border-radius: 12px;
      margin-bottom: 12px;
    }

    .order-item:last-child {
      margin-bottom: 0;
    }

    .order-item img {
      width: 70px;
      height: 70px;
      object-fit: contain;
      border-radius: 10px;
      background: #fff;
    }

    .item-details {
      flex: 1;
    }

    .item-details h4 {
      font-size: 15px;
      margin-bottom: 6px;
      color: #2B2B2B;
    }

    .item-details p {
      font-size: 13px;
      color: #868e96;
      margin: 0;
    }

    .item-price {
      font-weight: 700;
      font-size: 16px;
      color: #E5332A;
    }

    .order-totals {
      border-top: 2px solid #e9ecef;
      padding-top: 16px;
    }

    .total-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
      font-size: 14px;
    }

    .total-row span {
      color: #495057;
    }

    .total-row.final {
      font-size: 18px;
      font-weight: 700;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 2px solid #e9ecef;
    }

    .total-row.final span {
      color: #2B2B2B;
    }

    .total-row.final strong {
      color: #E5332A;
      font-size: 22px;
    }

    .no-order-section {
      text-align: center;
      padding: 60px 20px;
      background: #fff;
      border-radius: 20px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }

    .no-order-section svg {
      color: #ef4444;
      margin-bottom: 20px;
    }

    .no-order-section h3 {
      font-size: 24px;
      color: #2B2B2B;
      margin-bottom: 12px;
    }

    .no-order-section p {
      font-size: 16px;
      color: #868e96;
      margin-bottom: 8px;
    }

    .no-order-section .hint {
      font-size: 14px;
      color: #adb5bd;
    }

    /* ✅ MODAL STYLES */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2000;
      padding: 20px;
      animation: fadeInModal 0.2s ease;
    }

    @keyframes fadeInModal {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .modal-content {
      background: #fff;
      border-radius: 24px;
      max-width: 600px;
      width: 100%;
      max-height: 90vh;
      overflow-y: auto;
      animation: slideUp 0.3s ease;
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(40px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px 28px;
      border-bottom: 2px solid #f8f9fa;
    }

    .modal-header h2 {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 22px;
      color: #2B2B2B;
    }

    .modal-close {
      background: none;
      border: none;
      color: #868e96;
      cursor: pointer;
      padding: 8px;
      border-radius: 8px;
      transition: all 0.2s;
    }

    .modal-close:hover {
      background: #f8f9fa;
      color: #2B2B2B;
    }

    .modal-body {
      padding: 28px;
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 20px 28px;
      border-top: 2px solid #f8f9fa;
    }

    .checkbox-group {
      margin-bottom: 20px;
    }

    .checkbox-group label {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      font-weight: 500;
    }

    .checkbox-group input[type="checkbox"] {
      width: 20px;
      height: 20px;
      cursor: pointer;
    }

    .info-box,
    .warning-box {
      display: flex;
      gap: 12px;
      padding: 16px;
      border-radius: 12px;
      margin-bottom: 20px;
    }

    .info-box {
      background: #eff6ff;
      border: 2px solid #3b82f6;
      color: #1e40af;
    }

    .warning-box {
      background: #fffbeb;
      border: 2px solid #f59e0b;
      color: #92400e;
    }

    .info-box.warning {
      background: #fffbeb;
      border: 2px solid #f59e0b;
      color: #92400e;
    }

    .info-box svg,
    .warning-box svg {
      flex-shrink: 0;
      margin-top: 2px;
    }

    .info-box p,
    .warning-box p {
      margin: 0;
      font-size: 14px;
      line-height: 1.6;
    }

    .info-box strong,
    .warning-box strong {
      display: block;
      margin-bottom: 8px;
    }

    .warning-box ol {
      margin-top: 8px;
      padding-left: 20px;
    }

    .warning-box li {
      margin-bottom: 4px;
    }

    .small-text {
      font-size: 13px !important;
      opacity: 0.8;
    }

    .btn-primary,
    .btn-secondary {
      padding: 12px 24px;
      border: none;
      border-radius: 12px;
      font-weight: 600;
      font-size: 15px;
      cursor: pointer;
      transition: all 0.2s;
    }

    .btn-primary {
      background: #E5332A;
      color: #fff;
    }

    .btn-primary:hover:not(:disabled) {
      background: #c72a22;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(229, 51, 42, 0.3);
    }

    .btn-secondary {
      background: #f8f9fa;
      color: #495057;
    }

    .btn-secondary:hover:not(:disabled) {
      background: #e9ecef;
    }

    .btn-primary:disabled,
    .btn-secondary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Responsive */
    @media (max-width: 992px) {
      .details-grid {
        grid-template-columns: 1fr;
      }

      .details-card.timeline-card {
        grid-row: auto;
      }

      .search-section {
        padding: 32px 24px;
      }

      .header-icon {
        width: 64px;
        height: 64px;
      }

      .search-header h1 {
        font-size: 24px;
      }
    }

    @media (max-width: 768px) {
      .container {
        padding: 0 16px;
      }

      .search-section {
        padding: 24px 20px;
        border-radius: 20px;
      }

      .search-header h1 {
        font-size: 22px;
      }

      .search-header p {
        font-size: 14px;
      }

      .status-banner {
        flex-direction: column;
        text-align: center;
      }

      .action-buttons {
        margin-left: 0;
        width: 100%;
      }

      .return-btn {
        width: 100%;
        justify-content: center;
      }

      .details-card {
        padding: 20px;
      }

      .order-item {
        flex-direction: column;
        text-align: center;
      }

      .item-price {
        width: 100%;
        text-align: center;
      }

      .modal-content {
        max-height: 85vh;
      }

      .modal-header {
        padding: 20px;
      }

      .modal-header h2 {
        font-size: 18px;
      }

      .modal-body {
        padding: 20px;
      }

      .modal-footer {
        padding: 16px 20px;
        flex-direction: column;
      }

      .btn-primary,
      .btn-secondary {
        width: 100%;
      }
    }
  `}</style>;
}